// [Codex 2026-07-11] S134 preserves typed run parts across live and resumed streams.
import type { DynamicToolUIPart, UIMessage } from "ai";

export function reduceAssistantUiEvent(
  message: UIMessage,
  event: Record<string, unknown>,
): UIMessage {
  const type = stringValue(event.type);
  if (!type) return message;
  if (type === "text-delta") {
    return appendTextPart(message, "text", stringValue(event.delta) ?? "");
  }
  if (type === "reasoning-delta") {
    return appendTextPart(message, "reasoning", stringValue(event.delta) ?? "");
  }
  if (type === "text-end" || type === "reasoning-end") {
    return finishTextPart(message, type === "text-end" ? "text" : "reasoning");
  }
  if (type.startsWith("tool-")) return updateToolPart(message, event);
  return message;
}

function appendTextPart(
  message: UIMessage,
  type: "text" | "reasoning",
  delta: string,
): UIMessage {
  if (!delta) return message;
  const parts = [...message.parts];
  const index = findLastIndex(parts, (part) => part.type === type);
  if (index < 0) {
    parts.push({ type, text: delta, state: "streaming" });
  } else {
    const current = parts[index] as { type: typeof type; text: string };
    parts[index] = {
      ...current,
      text: current.text + delta,
      state: "streaming",
    };
  }
  return { ...message, parts };
}

function finishTextPart(
  message: UIMessage,
  type: "text" | "reasoning",
): UIMessage {
  const parts = message.parts.map((part) =>
    part.type === type ? { ...part, state: "done" as const } : part,
  );
  return { ...message, parts };
}

function updateToolPart(
  message: UIMessage,
  event: Record<string, unknown>,
): UIMessage {
  const toolCallId = stringValue(event.toolCallId);
  if (!toolCallId) return message;
  const parts = [...message.parts];
  const index = parts.findIndex(
    (part) => part.type === "dynamic-tool" && part.toolCallId === toolCallId,
  );
  const current =
    index >= 0 && parts[index]?.type === "dynamic-tool"
      ? (parts[index] as DynamicToolUIPart)
      : null;
  const type = stringValue(event.type);
  const toolName =
    stringValue(event.toolName) ?? current?.toolName ?? "Fintheon tool";
  let next: DynamicToolUIPart;

  if (type === "tool-output-available") {
    next = {
      type: "dynamic-tool",
      toolCallId,
      toolName,
      state: "output-available",
      input: current?.input ?? {},
      output: event.output,
    };
  } else if (type === "tool-input-available") {
    next = {
      type: "dynamic-tool",
      toolCallId,
      toolName,
      state: "input-available",
      input: event.input ?? {},
    };
  } else {
    const inputDelta = stringValue(event.inputTextDelta) ?? "";
    next = {
      type: "dynamic-tool",
      toolCallId,
      toolName,
      state: "input-streaming",
      input: inputDelta || current?.input,
    };
  }

  if (index >= 0) parts[index] = next;
  else parts.push(next);
  return { ...message, parts };
}

function findLastIndex<T>(items: T[], predicate: (item: T) => boolean): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return index;
  }
  return -1;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}
